/** Yeastar extension online_status device entry. */
export type OnlineDeviceStatus = {
  status?: number;
  ext_dev_type?: string;
  status_list?: Array<{ status?: number; ip?: string; linkus_dev_type?: string }>;
};

export type OnlineStatus = Record<string, OnlineDeviceStatus | undefined>;

const DEVICE_LABELS: Record<string, string> = {
  sip_phone: 'SIP',
  linkus_desktop: 'Desktop',
  linkus_mobile: 'Mobile',
  linkus_web: 'Web',
};

/**
 * Yeastar marks SIP registration on status_list[], not always top-level status.
 * Linkus clients use top-level status (0 offline / 1 online).
 * @see https://help.yeastar.com/en/p-series-cloud-edition/developer-guide/query-extension-list.html
 */
export function isDeviceOnline(device?: OnlineDeviceStatus | null): boolean {
  if (!device || typeof device !== 'object') return false;
  if (device.status === 1) return true;
  if (Array.isArray(device.status_list)) {
    return device.status_list.some((row) => row?.status === 1);
  }
  return false;
}

export function isExtensionRegistered(onlineStatus?: OnlineStatus): boolean {
  if (!onlineStatus || typeof onlineStatus !== 'object') return false;
  return Object.values(onlineStatus).some((device) => isDeviceOnline(device));
}

export function onlineDeviceCount(onlineStatus?: OnlineStatus): number {
  if (!onlineStatus || typeof onlineStatus !== 'object') return 0;
  return Object.values(onlineStatus).filter((device) => isDeviceOnline(device)).length;
}

export function onlineDeviceChips(onlineStatus?: OnlineStatus): string[] {
  if (!onlineStatus || typeof onlineStatus !== 'object') return [];
  return Object.entries(onlineStatus)
    .filter(([, device]) => isDeviceOnline(device))
    .map(([key]) => DEVICE_LABELS[key] ?? key);
}

/** Human-readable labels for Yeastar presence_status values. */
const PRESENCE_LABELS: Record<string, string> = {
  available: 'Available',
  away: 'Away',
  business_trip: 'Business Trip',
  do_not_disturb: 'Do Not Disturb',
  lunch: 'Lunch',
  off_work: 'Off Work',
};

/**
 * Format PBX presence for display.
 * Prefers custom_presence_status when the PBX provides a custom label.
 */
export function formatPresenceStatus(
  presenceStatus?: string | null,
  customPresenceStatus?: string | null
): string {
  const custom = (customPresenceStatus || '').trim();
  if (custom) return custom;
  const raw = (presenceStatus || '').trim();
  if (!raw) return '—';
  const key = raw.toLowerCase().replace(/\s+/g, '_');
  if (PRESENCE_LABELS[key]) return PRESENCE_LABELS[key];
  // Fallback: title-case snake_case / kebab-case
  return raw
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export type ExtensionRegistrationRow = {
  id: number;
  number: string;
  first_name?: string;
  caller_id_name?: string;
  online_status?: OnlineStatus;
};

export function summarizeRegistration(extensions: ExtensionRegistrationRow[]) {
  const registeredRows = extensions.filter((ext) => isExtensionRegistered(ext.online_status));
  const unregistered = extensions.filter((ext) => !isExtensionRegistered(ext.online_status));
  return {
    total: extensions.length,
    registered: registeredRows.length,
    unregistered,
  };
}

export function sortExtensionsByRegistration<T extends ExtensionRegistrationRow>(extensions: T[]) {
  return extensions.slice().sort((a, b) => {
    const aRegistered = isExtensionRegistered(a.online_status);
    const bRegistered = isExtensionRegistered(b.online_status);
    if (aRegistered !== bRegistered) return aRegistered ? 1 : -1;
    return a.number.localeCompare(b.number, undefined, { numeric: true });
  });
}
